import { SchedulingPage, SchedulingService } from "../types/newReportTypes";

/**
 * Available content height in PageLayout
 * Total page height: 843px
 * - Header: ~35px
 * - Footer: ~45px
 * = Available: 763px
 */
export const AVAILABLE_CONTENT_HEIGHT = 755;

// Fixed heights for scheduling page (in px) - measured from actual rendered elements
export const SCHEDULING_HEIGHTS = {
  TITLE_HEIGHT: 47, // PageTitleSection
  SECTION_HEADER_HEIGHT: 14, // "Weekly Schedule" / "Quarterly Schedule"
  TABLE_HEADER_HEIGHT: 43, // Table header row
  SERVICE_ROW_HEIGHT: 36, // Each service row
  CATEGORY_OVERHEAD: 0, // No extra overhead - already included in row height
  PADDING_GAPS: 58, // Page padding (20px top + 20px bottom) + gaps (18px)
};

type GroupedServices = {
  serviceTypeCode: number;
  serviceTypeName: string;
  services: SchedulingService[];
};

/**
 * Calculates the number of pages needed to render a SchedulingPage
 */
export const calculateSchedulingPageCount = (page: SchedulingPage): number => {
  const {
    TITLE_HEIGHT,
    SECTION_HEADER_HEIGHT,
    TABLE_HEADER_HEIGHT,
    SERVICE_ROW_HEIGHT,
    CATEGORY_OVERHEAD,
    PADDING_GAPS,
  } = SCHEDULING_HEIGHTS;

  // Group services by scheduling type and serviceTypeCode
  const groupServicesByType = (schedulingType: 1 | 2): GroupedServices[] => {
    const filteredServices = page.schedule.filter(
      (service) => service.schedulingType === schedulingType
    );

    const grouped = filteredServices.reduce((acc, service) => {
      const code = service.serviceTypeCode;
      if (!acc[code]) {
        acc[code] = [];
      }
      acc[code].push(service);
      return acc;
    }, {} as Record<number, SchedulingService[]>);

    return Object.entries(grouped).map(([code, services]) => ({
      serviceTypeCode: Number(code),
      serviceTypeName:
        page.labels.serviceTypes[
          Number(code) as keyof typeof page.labels.serviceTypes
        ] || "Other",
      services,
    }));
  };

  const allWeeklyGroups = groupServicesByType(1);
  const allQuarterlyGroups = groupServicesByType(2);

  let pageCount = 0;
  let currentHeight = TITLE_HEIGHT + PADDING_GAPS;

  // Helper to split services within a group
  const splitGroupServices = (
    group: GroupedServices,
    availableHeight: number,
    includeSectionHeader: boolean
  ): {
    fitted: GroupedServices | null;
    remaining: GroupedServices | null;
  } => {
    const sectionOverhead = includeSectionHeader
      ? SECTION_HEADER_HEIGHT + TABLE_HEADER_HEIGHT
      : 0;

    const heightForServices =
      availableHeight - sectionOverhead - CATEGORY_OVERHEAD;
    const maxServices = Math.floor(heightForServices / SERVICE_ROW_HEIGHT);

    if (maxServices <= 0) {
      return { fitted: null, remaining: group };
    }

    if (maxServices >= group.services.length) {
      return { fitted: group, remaining: null };
    }

    return {
      fitted: {
        ...group,
        services: group.services.slice(0, maxServices),
      },
      remaining: {
        ...group,
        services: group.services.slice(maxServices),
      },
    };
  };

  // Process a section (weekly or quarterly)
  const addSection = (
    groups: GroupedServices[],
    hasContent: { value: boolean }
  ): GroupedServices[] => {
    if (groups.length === 0) return [];

    const remaining: GroupedServices[] = [];
    let sectionStarted = false;

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const sectionOverhead = !sectionStarted
        ? SECTION_HEADER_HEIGHT + TABLE_HEADER_HEIGHT
        : 0;

      const availableSpace = AVAILABLE_CONTENT_HEIGHT - currentHeight;
      const { fitted, remaining: leftover } = splitGroupServices(
        group,
        availableSpace,
        !sectionStarted
      );

      if (fitted) {
        const fittedHeight =
          sectionOverhead +
          CATEGORY_OVERHEAD +
          fitted.services.length * SERVICE_ROW_HEIGHT;

        currentHeight += fittedHeight;
        sectionStarted = true;
        hasContent.value = true;
      }

      if (leftover) {
        remaining.push(leftover);
        remaining.push(...groups.slice(i + 1));
        break;
      }
    }

    return remaining;
  };

  // Process first page
  const hasContent = { value: false };
  let remainingWeekly = addSection(allWeeklyGroups, hasContent);
  let remainingQuarterly = addSection(allQuarterlyGroups, hasContent);

  if (hasContent.value) {
    pageCount++;
  }

  // Process remaining content across additional pages
  let maxIterations = 50; // Safety limit
  while (
    (remainingWeekly.length > 0 || remainingQuarterly.length > 0) &&
    maxIterations-- > 0
  ) {
    currentHeight = PADDING_GAPS; // Continuation pages have no title, only padding
    hasContent.value = false;

    remainingWeekly = addSection(remainingWeekly, hasContent);
    remainingQuarterly = addSection(remainingQuarterly, hasContent);

    if (hasContent.value) {
      pageCount++;
    } else {
      // No content added - prevent infinite loop
      break;
    }
  }

  return pageCount > 0 ? pageCount : 1;
};
